import { ApiClientError } from "@oms/api-client";
import { Button } from "@oms/ui";
import { newPasswordSchema, PASSWORD_MIN_LENGTH } from "@oms/validation";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  AuthError,
  AuthShell,
  AuthSubmitButton,
} from "@/components/auth/auth-shell";
import { authApi } from "@/lib/api";

interface AcceptInviteSearch {
  token?: string;
}

export const Route = createFileRoute("/accept-invite")({
  validateSearch: (search: Record<string, unknown>): AcceptInviteSearch => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
  component: AcceptInvitePage,
});

/**
 * Where an emailed invite link lands. The token is validated before anything
 * is collected, so a used, revoked or expired link explains itself rather than
 * dead-ending someone in a form they cannot submit.
 */
function AcceptInvitePage() {
  const { token } = Route.useSearch();

  const preview = useQuery({
    queryKey: ["invitation", token],
    queryFn: async () => (await authApi.validateInvitation(token ?? "")).data,
    enabled: Boolean(token),
    retry: false,
  });

  if (!token) {
    return (
      <InviteProblem
        title="Invitation link is incomplete"
        message="This link is missing its token. Use the button in your invitation email."
      />
    );
  }

  if (preview.isLoading) {
    return (
      <AuthShell title="Checking your invitation" subtitle="One moment…">
        <div className="h-24 animate-pulse rounded-lg bg-surface-hover" />
      </AuthShell>
    );
  }

  if (preview.isError || !preview.data) {
    return <InviteProblem {...describeInviteError(preview.error)} />;
  }

  return <SetPasswordForm token={token} invitation={preview.data} />;
}

type InvitationPreview = NonNullable<
  Awaited<ReturnType<typeof authApi.validateInvitation>>["data"]
>;

/**
 * Setting a password is the whole acceptance step: the emailed link proves
 * control of the address, so it works the same whether or not an account
 * already exists for it. On success the invitation is marked accepted and the
 * user is sent to the login page to sign in with what they just chose.
 */
function SetPasswordForm({
  token,
  invitation,
}: {
  token: string;
  invitation: InvitationPreview;
}) {
  const navigate = useNavigate();
  const [name, setName] = useState(invitation.name ?? "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(invitation.name ?? "");
  }, [invitation.name]);

  const setPasswordMutation = useMutation({
    mutationFn: async () =>
      authApi.setInvitationPassword({
        token,
        name: name.trim() || undefined,
        password,
      }),
    onSuccess: () => {
      void navigate({ to: "/login", search: { invited: invitation.email } });
    },
    onError: (err: unknown) => {
      setError(
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Could not set your password",
      );
    },
  });

  const submit = () => {
    setError(null);
    const parsed = newPasswordSchema.safeParse(password);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Choose a stronger password");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setPasswordMutation.mutate();
  };

  return (
    <AuthShell
      title={`Join ${invitation.organizationName}`}
      subtitle={`Set a password for ${invitation.email} to join as ${invitation.roleName}`}
      footer={
        <>
          This link expires {formatExpiry(invitation.expiresAt)} and can only be
          used once.
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {error ? <AuthError message={error} /> : null}

        <div className="space-y-1">
          <label className="text-sm font-medium text-text-secondary" htmlFor="email">
            Email
          </label>
          <input
            id="email"
            value={invitation.email}
            readOnly
            className="w-full cursor-not-allowed rounded-lg border border-border bg-surface-muted px-3 py-2 text-sm text-text-secondary"
          />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-text-secondary" htmlFor="name">
            Full name
          </label>
          <input
            id="name"
            className="w-full rounded-lg border border-border-strong px-3 py-2 text-sm outline-none ring-brand-500 focus:ring-2"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        <div className="space-y-1">
          <label
            className="text-sm font-medium text-text-secondary"
            htmlFor="password"
          >
            New password
          </label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            minLength={PASSWORD_MIN_LENGTH}
            className="w-full rounded-lg border border-border-strong px-3 py-2 text-sm outline-none ring-brand-500 focus:ring-2"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <p className="text-xs text-text-muted">
            At least {PASSWORD_MIN_LENGTH} characters. A few unrelated words work well.
          </p>
        </div>

        <div className="space-y-1">
          <label
            className="text-sm font-medium text-text-secondary"
            htmlFor="confirmPassword"
          >
            Confirm password
          </label>
          <input
            id="confirmPassword"
            type="password"
            autoComplete="new-password"
            minLength={PASSWORD_MIN_LENGTH}
            className="w-full rounded-lg border border-border-strong px-3 py-2 text-sm outline-none ring-brand-500 focus:ring-2"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </div>

        <AuthSubmitButton loading={setPasswordMutation.isPending}>
          Set password &amp; join
        </AuthSubmitButton>
      </form>
    </AuthShell>
  );
}

function InviteProblem({
  title,
  message,
}: {
  title: string;
  message: string;
}) {
  return (
    <AuthShell title={title} subtitle="This link cannot be used">
      <p className="text-sm text-text-secondary">{message}</p>
      <Link to="/login">
        <Button variant="secondary" className="w-full">
          Go to sign in
        </Button>
      </Link>
    </AuthShell>
  );
}

/** Turns the API's refusal into something the recipient can act on. */
function describeInviteError(error: unknown): {
  title: string;
  message: string;
} {
  const code = error instanceof ApiClientError ? error.code : "";
  switch (code) {
    case "INVITATION_USED":
      return {
        title: "This invitation was already used",
        message:
          "Invite links work exactly once. Sign in with the password you set, or ask an admin to send a new invitation.",
      };
    case "INVITATION_REVOKED":
      return {
        title: "This invitation was cancelled",
        message:
          "An admin revoked this invite. Ask them to send you a new one.",
      };
    case "INVITATION_EXPIRED":
      return {
        title: "This invitation has expired",
        message:
          "Invite links are valid for 48 hours. Ask an admin to send a fresh invitation.",
      };
    default:
      return {
        title: "Invitation link is not valid",
        message:
          "We could not find this invitation. Check that you opened the most recent link from your email.",
      };
  }
}

/** "in 47 hours" / "in 2 days", from the Unix timestamp the API returns. */
function formatExpiry(expiresAt: number): string {
  const hoursLeft = Math.round((expiresAt * 1000 - Date.now()) / 3_600_000);
  if (hoursLeft <= 0) return "shortly";
  if (hoursLeft < 24) return `in ${hoursLeft} hours`;
  return `in ${Math.round(hoursLeft / 24)} days`;
}
